// The requested/confirmed dual-tier LOD controller (design D9 / CF-AR4). It tracks a
// full `{ viewport, tier }` on each side: `requested.tier` follows zoom via the
// PROTOCOL §4 ±10% hysteresis, `requested.viewport` follows the camera rect. Both zoom
// (tier) and pan/resize (viewport-only) changes are debounced/coalesced into at most
// one outstanding `Subscribe`. `confirmed` advances only on a keyframe whose tier
// matches the outstanding request; a wrong-tier keyframe is ignored. A tier change
// triggers a crossfade; a viewport-only confirmation re-keys without one.

import type { Subscribe, Tier, ViewportRect } from './messages';
import type { Scheduler } from './connection';

export { Tier } from './messages';

export interface Subscription {
  viewport: ViewportRect;
  tier: Tier;
}

/** What a confirming keyframe did to the confirmed subscription. */
export type ConfirmKind = 'tier-change' | 'viewport-only';

export interface LodControllerConfig {
  /** Debounce window that coalesces a burst of zoom/pan events into one Subscribe. */
  coalesceMs: number;
  /** Fractional viewport epsilon below which a viewport change is not resubscribed. */
  viewportEpsilon: number;
}

export interface LodControllerDeps {
  scheduler: Scheduler;
  /** Maps a zoom level to a tier with ±10% hysteresis around the threshold. */
  tierForZoom(zoom: number, current: Tier): Tier;
}

export interface LodCallbacks {
  /** Emitted (coalesced) when a new `Subscribe` should be sent. */
  onSubscribe?(sub: Subscribe): void;
  /** Emitted when a confirming keyframe changed the tier (drives the crossfade). */
  onTierChange?(from: Tier, to: Tier): void;
}

/** Returns true when `a` and `b` differ by more than `epsilon` (a fraction of the
 * viewport's own width/height) in any edge — a viewport-equality check that scales
 * with zoom rather than comparing raw world units. */
function viewportChanged(a: ViewportRect, b: ViewportRect, epsilon: number): boolean {
  const width = Math.max(a.max[0] - a.min[0], 1e-9);
  const height = Math.max(a.max[1] - a.min[1], 1e-9);
  const dMinX = Math.abs(a.min[0] - b.min[0]) / width;
  const dMinY = Math.abs(a.min[1] - b.min[1]) / height;
  const dMaxX = Math.abs(a.max[0] - b.max[0]) / width;
  const dMaxY = Math.abs(a.max[1] - b.max[1]) / height;
  return Math.max(dMinX, dMinY, dMaxX, dMaxY) > epsilon;
}

export class LodController {
  private requestedState: Subscription;
  private confirmedState: Subscription | null = null;
  private lastSent: Subscription | null = null;
  private timerId: number | null = null;

  constructor(
    initial: Subscription,
    private readonly config: LodControllerConfig,
    private readonly deps: LodControllerDeps,
    private readonly callbacks: LodCallbacks = {},
  ) {
    this.requestedState = { viewport: initial.viewport, tier: initial.tier };
  }

  requested(): Subscription {
    return { viewport: this.requestedState.viewport, tier: this.requestedState.tier };
  }

  confirmed(): Subscription | null {
    return this.confirmedState === null
      ? null
      : { viewport: this.confirmedState.viewport, tier: this.confirmedState.tier };
  }

  setZoom(zoom: number): void {
    const newTier = this.deps.tierForZoom(zoom, this.requestedState.tier);
    if (newTier === this.requestedState.tier) {
      return; // hysteresis held: no tier change
    }
    this.requestedState = { viewport: this.requestedState.viewport, tier: newTier };
    this.scheduleSubscribe();
  }

  setViewport(viewport: ViewportRect): void {
    if (!viewportChanged(viewport, this.requestedState.viewport, this.config.viewportEpsilon)) {
      return;
    }
    this.requestedState = { viewport, tier: this.requestedState.tier };
    this.scheduleSubscribe();
  }

  /** Directly requests a tier (design D9 / DD4): the composition root's per-route
   * LOD seam forwards a route's tier intent here (the Match screen requests `Wide`
   * on entry) without routing through a zoom value. Idempotent — a request for the
   * already-requested tier is a no-op; otherwise it updates `requested.tier` and
   * schedules the coalesced `Subscribe`, exactly as `setZoom` does after crossing
   * the hysteresis threshold. */
  requestTier(tier: Tier): void {
    if (tier === this.requestedState.tier) {
      return;
    }
    this.requestedState = { viewport: this.requestedState.viewport, tier };
    this.scheduleSubscribe();
  }

  /** Consulted by the connection on a keyframe: confirm (matching tier) or ignore. */
  keyframeDisposition(tier: Tier): 'confirm' | 'ignore' {
    return tier === this.requestedState.tier ? 'confirm' : 'ignore';
  }

  /** Advance `confirmed` on a matching-tier keyframe; returns the confirm kind. */
  confirmKeyframe(tier: Tier): ConfirmKind {
    const previous = this.confirmedState;
    const kind: ConfirmKind = previous === null || previous.tier !== tier ? 'tier-change' : 'viewport-only';
    if (kind === 'tier-change' && previous !== null) {
      this.callbacks.onTierChange?.(previous.tier, tier);
    }
    this.confirmedState = { viewport: this.requestedState.viewport, tier };
    return kind;
  }

  // --- coalesced Subscribe emission ------------------------------------------------

  private scheduleSubscribe(): void {
    if (this.timerId !== null) {
      // A debounce window is already open: the timer reads `requestedState` live
      // when it fires, so this later change is coalesced into that single emission
      // rather than queued or re-armed (design D9: "send the latest, not a queue").
      return;
    }
    this.timerId = this.deps.scheduler.setTimer(() => {
      this.timerId = null;
      this.maybeEmitSubscribe();
    }, this.config.coalesceMs);
  }

  private maybeEmitSubscribe(): void {
    const current: Subscription = {
      viewport: this.requestedState.viewport,
      tier: this.requestedState.tier,
    };
    if (
      this.lastSent !== null &&
      current.tier === this.lastSent.tier &&
      !viewportChanged(current.viewport, this.lastSent.viewport, this.config.viewportEpsilon)
    ) {
      return; // settled back to what was already (or is about to be) sent
    }
    this.lastSent = current;
    this.callbacks.onSubscribe?.({ viewport: current.viewport, tier: current.tier });
  }
}
